import { useMemo } from 'react';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type {
  Action,
  ExecutionWithAction,
  CaptureSessionWithAction,
  CaptureLogEntry,
  ExecutionStatus,
  CaptureSessionStatus,
  Client,
} from '@databro/shared';

// Unified Activity type
export type ActivityType = 'execution' | 'capture' | 'batch';

export interface Activity {
  id: string;
  type: ActivityType;
  status: ExecutionStatus | CaptureSessionStatus | string;
  createdAt: string;
  completedAt?: string;
  error?: string;
  execution?: ExecutionWithAction;
  capture?: CaptureSessionWithAction;
  // Batch-specific fields
  batchId?: string;
  batchLabel?: string;
  batchTotal?: number;
  batchCompleted?: number;
  batchFailed?: number;
  batchExecutions?: ExecutionWithAction[];
}

interface ActionDetailState {
  // Core data
  actionId: string | null;
  action: Action | null;
  executions: ExecutionWithAction[];
  captures: CaptureSessionWithAction[];
  clients: Client[];

  // Loading states
  isActionLoading: boolean;
  isExecutionsLoading: boolean;
  isCapturesLoading: boolean;

  // UI state
  selectedActivity: Activity | null;
  runModalOpen: boolean;
  editModalOpen: boolean;
  errorDialog: { open: boolean; title: string; message: string };

  // Realtime capture logs (keyed by capture session ID)
  realtimeCaptureLogs: Map<string, CaptureLogEntry[]>;

  // Mutation states
  isCapturing: boolean;
  isRunning: boolean;
  isStoppingCapture: boolean;
  isCancellingExecution: boolean;
}

interface ActionDetailActions {
  // Setters for data
  setActionId: (id: string | null) => void;
  setAction: (action: Action | null) => void;
  setExecutions: (executions: ExecutionWithAction[]) => void;
  setCaptures: (captures: CaptureSessionWithAction[]) => void;
  setClients: (clients: Client[]) => void;

  // Loading setters
  setActionLoading: (loading: boolean) => void;
  setExecutionsLoading: (loading: boolean) => void;
  setCapturesLoading: (loading: boolean) => void;

  // UI actions
  setSelectedActivity: (activity: Activity | null) => void;
  selectActivityById: (id: string, type: ActivityType) => void;
  setRunModalOpen: (open: boolean) => void;
  setEditModalOpen: (open: boolean) => void;
  setErrorDialog: (dialog: { open: boolean; title: string; message: string }) => void;
  showError: (title: string, message: string) => void;
  closeErrorDialog: () => void;

  // Realtime logs
  initRealtimeLogs: (captureSessionId: string) => void;
  addRealtimeLog: (captureSessionId: string, log: CaptureLogEntry) => void;
  clearRealtimeLogs: (captureSessionId: string) => void;

  // Mutation states
  setCapturing: (capturing: boolean) => void;
  setRunning: (running: boolean) => void;
  setStoppingCapture: (stopping: boolean) => void;
  setCancellingExecution: (cancelling: boolean) => void;

  // Reset
  reset: () => void;
}

// Computed selectors (derived state)
interface ActionDetailComputed {
  // Unified activity list sorted by createdAt desc
  activities: Activity[];

  // Active states derived from data
  activeCapture: CaptureSessionWithAction | null;
  activeExecution: ExecutionWithAction | null;
  hasActiveCapture: boolean;
  hasActiveExecution: boolean;

  // Credentials status
  hasCredentials: boolean;

  // Bound client info
  boundClient: Client | null;

  // Loading
  isLoading: boolean;
}

const initialState: ActionDetailState = {
  actionId: null,
  action: null,
  executions: [],
  captures: [],
  clients: [],
  isActionLoading: false,
  isExecutionsLoading: false,
  isCapturesLoading: false,
  selectedActivity: null,
  runModalOpen: false,
  editModalOpen: false,
  errorDialog: { open: false, title: '', message: '' },
  realtimeCaptureLogs: new Map(),
  isCapturing: false,
  isRunning: false,
  isStoppingCapture: false,
  isCancellingExecution: false,
};

export const useActionDetailStore = create<ActionDetailState & ActionDetailActions>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    // Setters for data
    setActionId: (id) => set({ actionId: id }),
    setAction: (action) => set({ action }),
    setExecutions: (executions) => set({ executions }),
    setCaptures: (captures) => set({ captures }),
    setClients: (clients) => set({ clients }),

    // Loading setters
    setActionLoading: (loading) => set({ isActionLoading: loading }),
    setExecutionsLoading: (loading) => set({ isExecutionsLoading: loading }),
    setCapturesLoading: (loading) => set({ isCapturesLoading: loading }),

    // UI actions
    setSelectedActivity: (activity) => set({ selectedActivity: activity }),
    selectActivityById: (id, type) => {
      const state = get();
      const activities = computeActivities(state);
      const found = activities.find((a) => a.id === id && a.type === type);
      if (found) {
        set({ selectedActivity: found });
      }
    },
    setRunModalOpen: (open) => set({ runModalOpen: open }),
    setEditModalOpen: (open) => set({ editModalOpen: open }),
    setErrorDialog: (dialog) => set({ errorDialog: dialog }),
    showError: (title, message) => set({ errorDialog: { open: true, title, message } }),
    closeErrorDialog: () =>
      set((state) => ({ errorDialog: { ...state.errorDialog, open: false } })),

    // Realtime logs
    initRealtimeLogs: (captureSessionId) =>
      set((state) => {
        const newMap = new Map(state.realtimeCaptureLogs);
        newMap.set(captureSessionId, []);
        return { realtimeCaptureLogs: newMap };
      }),
    addRealtimeLog: (captureSessionId, log) =>
      set((state) => {
        const newMap = new Map(state.realtimeCaptureLogs);
        const existing = newMap.get(captureSessionId) || [];
        newMap.set(captureSessionId, [...existing, log]);
        return { realtimeCaptureLogs: newMap };
      }),
    clearRealtimeLogs: (captureSessionId) =>
      set((state) => {
        const newMap = new Map(state.realtimeCaptureLogs);
        newMap.delete(captureSessionId);
        return { realtimeCaptureLogs: newMap };
      }),

    // Mutation states
    setCapturing: (capturing) => set({ isCapturing: capturing }),
    setRunning: (running) => set({ isRunning: running }),
    setStoppingCapture: (stopping) => set({ isStoppingCapture: stopping }),
    setCancellingExecution: (cancelling) => set({ isCancellingExecution: cancelling }),

    // Reset
    reset: () => set(initialState),
  }))
);

// Helper to compute activities from state
function computeActivities(state: ActionDetailState): Activity[] {
  const list: Activity[] = [];

  for (const exec of state.executions) {
    list.push({
      id: exec.id,
      type: 'execution',
      status: exec.status,
      createdAt: exec.createdAt,
      completedAt: exec.completedAt,
      error: exec.error,
      execution: exec,
    });
  }

  for (const cap of state.captures) {
    list.push({
      id: cap.id,
      type: 'capture',
      status: cap.status,
      createdAt: cap.createdAt,
      completedAt: cap.completedAt,
      error: cap.error,
      capture: cap,
    });
  }

  list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  return list;
}

// Selector hooks for computed values
export function useActivities(): Activity[] {
  const executions = useActionDetailStore((state) => state.executions);
  const captures = useActionDetailStore((state) => state.captures);

  return useMemo(() => {
    const list: Activity[] = [];

    // Group executions by batchId
    const batchMap = new Map<string, ExecutionWithAction[]>();
    const nonBatchExecutions: ExecutionWithAction[] = [];

    for (const exec of executions) {
      const batchId = (exec as any).batchId;
      if (batchId) {
        const existing = batchMap.get(batchId) || [];
        existing.push(exec);
        batchMap.set(batchId, existing);
      } else {
        nonBatchExecutions.push(exec);
      }
    }

    // Add batch activities
    for (const [batchId, batchExecs] of batchMap) {
      // Sort by batchIndex
      batchExecs.sort((a, b) => ((a as any).batchIndex ?? 0) - ((b as any).batchIndex ?? 0));

      // Calculate batch stats
      const completed = batchExecs.filter((e) => e.status === 'success').length;
      const failed = batchExecs.filter((e) => e.status === 'failed' || e.status === 'auth_required' || e.status === 'cancelled').length;
      const waiting = batchExecs.filter((e) => e.status === 'waiting_for_client').length;
      const total = batchExecs.length;

      // Determine batch status
      let batchStatus: string;
      const allDone = completed + failed === total;
      if (waiting > 0) {
        batchStatus = 'waiting';
      } else if (!allDone) {
        batchStatus = 'running';
      } else if (failed === 0) {
        batchStatus = 'completed';
      } else if (completed === 0) {
        batchStatus = 'failed';
      } else {
        batchStatus = 'partial';
      }

      // Use earliest createdAt for sorting
      const earliestCreatedAt = batchExecs.reduce(
        (min, e) => (e.createdAt < min ? e.createdAt : min),
        batchExecs[0].createdAt
      );

      list.push({
        id: batchId,
        type: 'batch',
        status: batchStatus,
        createdAt: earliestCreatedAt,
        batchId,
        batchTotal: total,
        batchCompleted: completed,
        batchFailed: failed,
        batchExecutions: batchExecs,
      });
    }

    // Add non-batch executions
    for (const exec of nonBatchExecutions) {
      list.push({
        id: exec.id,
        type: 'execution',
        status: exec.status,
        createdAt: exec.createdAt,
        completedAt: exec.completedAt,
        error: exec.error,
        execution: exec,
      });
    }

    // Add captures
    for (const cap of captures) {
      list.push({
        id: cap.id,
        type: 'capture',
        status: cap.status,
        createdAt: cap.createdAt,
        completedAt: cap.completedAt,
        error: cap.error,
        capture: cap,
      });
    }

    list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return list;
  }, [executions, captures]);
}

export function useActiveCapture(): CaptureSessionWithAction | null {
  return useActionDetailStore((state) =>
    state.captures.find((c) => c.status === 'pending' || c.status === 'capturing') ?? null
  );
}

export function useActiveExecution(): ExecutionWithAction | null {
  return useActionDetailStore((state) =>
    state.executions.find(
      (e) => e.status === 'pending' || e.status === 'queued' || e.status === 'running' || e.status === 'waiting_for_client'
    ) ?? null
  );
}

export function useHasActiveCapture(): boolean {
  return useActionDetailStore(
    (state) => state.captures.some((c) => c.status === 'pending' || c.status === 'capturing')
  );
}

export function useHasActiveExecution(): boolean {
  return useActionDetailStore(
    (state) =>
      state.executions.some(
        (e) => e.status === 'pending' || e.status === 'queued' || e.status === 'running' || e.status === 'waiting_for_client'
      )
  );
}

export function useHasCredentials(): boolean {
  return useActionDetailStore((state) => (state.action as any)?.hasCredentials !== false);
}

export function useBoundClients(): Client[] {
  const action = useActionDetailStore((state) => state.action);
  const clients = useActionDetailStore((state) => state.clients);

  return useMemo(() => {
    const actionClients = (action as any)?.clients || [];
    return clients.filter((c) => actionClients.some((ac: Client) => ac.id === c.id));
  }, [action, clients]);
}

export function useIsLoading(): boolean {
  return useActionDetailStore(
    (state) => state.isActionLoading || state.isExecutionsLoading || state.isCapturesLoading
  );
}

// Helper to map capture status to execution-like status for display
export function mapCaptureStatusForDisplay(status: CaptureSessionStatus): string {
  switch (status) {
    case 'pending':
      return 'pending';
    case 'capturing':
      return 'running';
    case 'success':
      return 'success';
    case 'failed':
      return 'failed';
    default:
      return status;
  }
}

// Check if an activity is currently live (has realtime logs)
export function useIsActivityLive(activityId: string, type: ActivityType): boolean {
  return useActionDetailStore((state) => {
    if (type !== 'capture') return false;
    const capture = state.captures.find((c) => c.id === activityId);
    if (!capture) return false;
    const isActive = capture.status === 'pending' || capture.status === 'capturing';
    const hasRealtimeLogs = state.realtimeCaptureLogs.has(activityId);
    return isActive && hasRealtimeLogs;
  });
}

// Get logs for a capture (realtime if available, otherwise from capture data)
export function useCaptureLogs(captureId: string): CaptureLogEntry[] {
  return useActionDetailStore((state) => {
    const realtimeLogs = state.realtimeCaptureLogs.get(captureId);
    if (realtimeLogs && realtimeLogs.length > 0) {
      return realtimeLogs;
    }
    const capture = state.captures.find((c) => c.id === captureId);
    return capture?.logs || [];
  });
}
